/**
 * Income Tax Calculator
 * Calculate income tax for Canada (CPP, EI) or USA (FICA)
 */

import React, { useState, useCallback } from 'react';
import { useTylerStateUpdate } from '../utils/TylerState';
import { useCountry } from '../contexts/CountryContext';
import StructuredData from './StructuredData';
import incomeTaxAPI, {
  CanadaTaxCalculationResponse,
  USATaxCalculationResponse,
  isCanadaResponse,
  isUSAResponse,
  TaxCalculationResponse,
  RRSPImpactScenario,
} from '../services/income-tax-api';
import InfoTooltip from './shared/InfoTooltip';
import '../styles/IncomeTaxCalculator.css';

const PROVINCES = [
  { code: 'ON', name: 'Ontario' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'AB', name: 'Alberta' },
  { code: 'QC', name: 'Quebec' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'YT', name: 'Yukon' },
];

const STATES = [
  // No income tax states
  { code: 'AK', name: 'Alaska', note: 'No state income tax' },
  { code: 'FL', name: 'Florida', note: 'No state income tax' },
  { code: 'NV', name: 'Nevada', note: 'No state income tax' },
  { code: 'NH', name: 'New Hampshire', note: 'No wage tax' },
  { code: 'SD', name: 'South Dakota', note: 'No state income tax' },
  { code: 'TN', name: 'Tennessee', note: 'No state income tax' },
  { code: 'TX', name: 'Texas', note: 'No state income tax' },
  { code: 'WA', name: 'Washington', note: 'No state income tax' },
  { code: 'WY', name: 'Wyoming', note: 'No state income tax' },
  // Flat rate states
  { code: 'AZ', name: 'Arizona', note: 'Flat 2.5%' },
  { code: 'CO', name: 'Colorado', note: 'Flat 4.4%' },
  { code: 'IL', name: 'Illinois', note: 'Flat 4.95%' },
  { code: 'IN', name: 'Indiana', note: 'Flat 3.05%' },
  { code: 'IA', name: 'Iowa', note: 'Flat 3.8%' },
  { code: 'KY', name: 'Kentucky', note: 'Flat 4%' },
  { code: 'MA', name: 'Massachusetts', note: 'Flat 5%' },
  { code: 'MI', name: 'Michigan', note: 'Flat 4.25%' },
  { code: 'NC', name: 'North Carolina', note: 'Flat 4.75%' },
  { code: 'PA', name: 'Pennsylvania', note: 'Flat 3.07%' },
  { code: 'UT', name: 'Utah', note: 'Flat 4.65%' },
  // Progressive rate states
  { code: 'AL', name: 'Alabama' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'KS', name: 'Kansas' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
].sort((a, b) => a.name.localeCompare(b.name));

const PROVINCE_CODE_TO_NAME: Record<string, string> = {
  'ON': 'Ontario',
  'BC': 'British Columbia',
  'AB': 'Alberta',
  'QC': 'Quebec',
  'MB': 'Manitoba',
  'SK': 'Saskatchewan',
  'NS': 'Nova Scotia',
  'NB': 'New Brunswick',
  'NL': 'Newfoundland and Labrador',
  'PE': 'Prince Edward Island',
  'NT': 'Northwest Territories',
  'NU': 'Nunavut',
  'YT': 'Yukon',
};

const STATE_CODE_TO_NAME: Record<string, string> = {
  'AK': 'Alaska',
  'AL': 'Alabama',
  'AR': 'Arkansas',
  'AZ': 'Arizona',
  'CA': 'California',
  'CO': 'Colorado',
  'CT': 'Connecticut',
  'DC': 'District of Columbia',
  'DE': 'Delaware',
  'FL': 'Florida',
  'GA': 'Georgia',
  'HI': 'Hawaii',
  'IA': 'Iowa',
  'ID': 'Idaho',
  'IL': 'Illinois',
  'IN': 'Indiana',
  'KS': 'Kansas',
  'KY': 'Kentucky',
  'LA': 'Louisiana',
  'MA': 'Massachusetts',
  'MD': 'Maryland',
  'ME': 'Maine',
  'MI': 'Michigan',
  'MN': 'Minnesota',
  'MO': 'Missouri',
  'MS': 'Mississippi',
  'MT': 'Montana',
  'NC': 'North Carolina',
  'ND': 'North Dakota',
  'NE': 'Nebraska',
  'NH': 'New Hampshire',
  'NJ': 'New Jersey',
  'NM': 'New Mexico',
  'NV': 'Nevada',
  'NY': 'New York',
  'OH': 'Ohio',
  'OK': 'Oklahoma',
  'OR': 'Oregon',
  'PA': 'Pennsylvania',
  'RI': 'Rhode Island',
  'SC': 'South Carolina',
  'SD': 'South Dakota',
  'TN': 'Tennessee',
  'TX': 'Texas',
  'UT': 'Utah',
  'VA': 'Virginia',
  'VT': 'Vermont',
  'WA': 'Washington',
  'WI': 'Wisconsin',
  'WV': 'West Virginia',
  'WY': 'Wyoming',
};

type ResultsTab = 'summary' | 'breakdown' | 'brackets' | 'rrsp-impact';

interface IncomeTaxCalculatorProps {
  embedded?: boolean;
  onBeforeCalculate?: (calculateFn: () => void) => void;
}

const IncomeTaxCalculator: React.FC<IncomeTaxCalculatorProps> = ({ embedded, onBeforeCalculate }) => {
  useTylerStateUpdate();
  const { country, isCanada, currencyCode } = useCountry();

  // Input state
  const [employmentIncome, setEmploymentIncome] = useState<string>('75000');
  const [selfEmploymentIncome, setSelfEmploymentIncome] = useState<string>('0');
  const [otherIncome, setOtherIncome] = useState<string>('0');
  const [capitalGains, setCapitalGains] = useState<string>('0');
  const [eligibleDividends, setEligibleDividends] = useState<string>('0');
  const [qualifiedDividends, setQualifiedDividends] = useState<string>('0');
  const [rrspContribution, setRrspContribution] = useState<string>('0');
  const [charitableDonations, setCharitableDonations] = useState<string>('0');
  const [retirementContribution, setRetirementContribution] = useState<string>('0');
  const [province, setProvince] = useState<string>('ON');
  const [usState, setUsState] = useState<string>('CA');
  const [filingStatus, setFilingStatus] = useState<'single' | 'married'>('single');
  const [age, setAge] = useState<string>('35');
  const [year, setYear] = useState<string>('2024');

  // Results state
  const [results, setResults] = useState<TaxCalculationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultsTab, setResultsTab] = useState<ResultsTab>('summary');

  // RRSP Impact state
  const [rrspImpactData, setRrspImpactData] = useState<RRSPImpactScenario[] | null>(null);
  const [rrspImpactLoading, setRrspImpactLoading] = useState(false);

  // Show advanced inputs
  const [showAdvanced, setShowAdvanced] = useState(false);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat(isCanada ? 'en-CA' : 'en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const parseNumber = (value: string): number => {
    const cleaned = value.replace(/[^0-9.-]/g, '');
    return parseFloat(cleaned) || 0;
  };

  const handleCalculate = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRrspImpactData(null); // Reset so next tab click re-fetches

    try {
      let response: TaxCalculationResponse;

      if (isCanada) {
        response = await incomeTaxAPI.calculateCanadaTax({
          employment_income: parseNumber(employmentIncome),
          self_employment_income: parseNumber(selfEmploymentIncome),
          other_income: parseNumber(otherIncome),
          capital_gains: parseNumber(capitalGains),
          eligible_dividends: parseNumber(eligibleDividends),
          rrsp_contribution: parseNumber(rrspContribution),
          charitable_donations: parseNumber(charitableDonations),
          province: PROVINCE_CODE_TO_NAME[province] || 'Ontario',
          age: parseInt(age) || 35,
          year: parseInt(year) || 2024,
        });
      } else {
        response = await incomeTaxAPI.calculateUSATax({
          employment_income: parseNumber(employmentIncome),
          self_employment_income: parseNumber(selfEmploymentIncome),
          other_income: parseNumber(otherIncome),
          capital_gains: parseNumber(capitalGains),
          qualified_dividends: parseNumber(qualifiedDividends),
          retirement_contribution: parseNumber(retirementContribution),
          state: STATE_CODE_TO_NAME[usState] || 'California',
          filing_status: filingStatus,
          year: parseInt(year) || 2024,
        });
      }

      setResults(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Calculation failed');
    } finally {
      setLoading(false);
    }
  }, [isCanada, employmentIncome, selfEmploymentIncome, otherIncome, capitalGains, eligibleDividends, qualifiedDividends, rrspContribution, charitableDonations, retirementContribution, province, usState, filingStatus, age, year]);

  const formatInputAsCurrency = (value: string, setter: (v: string) => void) => {
    const num = parseNumber(value);
    if (!isNaN(num)) {
      setter(num.toString());
    }
  };

  const renderInputSection = () => (
    <div className="tax-input-section">
      <h2>Income Details</h2>

      <div className="input-grid">
        <div className="input-group">
          <label htmlFor="employmentIncome">
            {isCanada ? 'Employment Income (T4)' : 'Wages (W-2)'}
            <InfoTooltip termKey="employment_income" />
          </label>
          <div className="input-with-prefix">
            <span className="prefix">$</span>
            <input
              type="text"
              id="employmentIncome"
              value={employmentIncome}
              onChange={(e) => setEmploymentIncome(e.target.value)}
              onBlur={() => formatInputAsCurrency(employmentIncome, setEmploymentIncome)}
              placeholder="75,000"
            />
          </div>
          <span className="input-hint">
            {isCanada ? 'Salary, wages, bonuses from employer' : 'Salary, wages, tips from employer'}
          </span>
        </div>

        {isCanada ? (
          <div className="input-group">
            <label htmlFor="province">Province</label>
            <select
              id="province"
              value={province}
              onChange={(e) => setProvince(e.target.value)}
            >
              {PROVINCES.map((p) => (
                <option key={p.code} value={p.code}>{p.name}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="input-group">
            <label htmlFor="usState">State</label>
            <select
              id="usState"
              value={usState}
              onChange={(e) => setUsState(e.target.value)}
            >
              {STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}{s.note ? ` (${s.note})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {isCanada ? (
          <div className="input-group">
            <label htmlFor="rrspContribution">
              RRSP Contribution
              <InfoTooltip termKey="rrsp_contribution" />
            </label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                type="text"
                id="rrspContribution"
                value={rrspContribution}
                onChange={(e) => setRrspContribution(e.target.value)}
                onBlur={() => formatInputAsCurrency(rrspContribution, setRrspContribution)}
                placeholder="0"
              />
            </div>
            <span className="input-hint">Tax-deductible retirement savings</span>
          </div>
        ) : (
          <div className="input-group">
            <label htmlFor="retirementContribution">
              401(k)/IRA Contribution
            </label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                type="text"
                id="retirementContribution"
                value={retirementContribution}
                onChange={(e) => setRetirementContribution(e.target.value)}
                onBlur={() => formatInputAsCurrency(retirementContribution, setRetirementContribution)}
                placeholder="0"
              />
            </div>
            <span className="input-hint">Pre-tax retirement contributions</span>
          </div>
        )}

        {/* Charitable Donations */}
        <div className="input-group">
          <label htmlFor="charitableDonations">
            Charitable Donations
          </label>
          <div className="input-with-prefix">
            <span className="prefix">$</span>
            <input
              type="text"
              id="charitableDonations"
              value={charitableDonations}
              onChange={(e) => setCharitableDonations(e.target.value)}
              onBlur={() => formatInputAsCurrency(charitableDonations, setCharitableDonations)}
              placeholder="0"
            />
          </div>
          <span className="input-hint">
            {isCanada ? 'Earns federal + provincial tax credits' : 'Deductible if you itemize'}
          </span>
        </div>

        {isCanada ? (
          <div className="input-group">
            <label htmlFor="age">Age</label>
            <input
              type="number"
              id="age"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              min="18"
              max="100"
            />
          </div>
        ) : (
          <div className="input-group">
            <label htmlFor="filingStatus">Filing Status</label>
            <select
              id="filingStatus"
              value={filingStatus}
              onChange={(e) => setFilingStatus(e.target.value as 'single' | 'married')}
            >
              <option value="single">Single</option>
              <option value="married">Married Filing Jointly</option>
            </select>
          </div>
        )}
      </div>

      <button
        className="toggle-advanced"
        onClick={() => setShowAdvanced(!showAdvanced)}
      >
        {showAdvanced ? '- Hide Advanced Options' : '+ Show Advanced Options'}
      </button>

      {showAdvanced && (
        <div className="advanced-inputs">
          <h3>Additional Income Sources</h3>
          <div className="input-grid">
            <div className="input-group">
              <label htmlFor="selfEmploymentIncome">
                Self-Employment Income
                <InfoTooltip termKey="self_employment_income" />
              </label>
              <div className="input-with-prefix">
                <span className="prefix">$</span>
                <input
                  type="text"
                  id="selfEmploymentIncome"
                  value={selfEmploymentIncome}
                  onChange={(e) => setSelfEmploymentIncome(e.target.value)}
                  onBlur={() => formatInputAsCurrency(selfEmploymentIncome, setSelfEmploymentIncome)}
                  placeholder="0"
                />
              </div>
              <span className="input-hint">
                {isCanada ? 'Business income, freelancing (pays both CPP portions)' : 'Business income, freelancing (pays self-employment tax)'}
              </span>
            </div>

            <div className="input-group">
              <label htmlFor="otherIncome">
                Other Income
                <InfoTooltip termKey="other_income" />
              </label>
              <div className="input-with-prefix">
                <span className="prefix">$</span>
                <input
                  type="text"
                  id="otherIncome"
                  value={otherIncome}
                  onChange={(e) => setOtherIncome(e.target.value)}
                  onBlur={() => formatInputAsCurrency(otherIncome, setOtherIncome)}
                  placeholder="0"
                />
              </div>
              <span className="input-hint">Interest, rental income, pensions, etc.</span>
            </div>

            <div className="input-group">
              <label htmlFor="capitalGains">
                Capital Gains
                <InfoTooltip termKey="capital_gains" />
              </label>
              <div className="input-with-prefix">
                <span className="prefix">$</span>
                <input
                  type="text"
                  id="capitalGains"
                  value={capitalGains}
                  onChange={(e) => setCapitalGains(e.target.value)}
                  onBlur={() => formatInputAsCurrency(capitalGains, setCapitalGains)}
                  placeholder="0"
                />
              </div>
              <span className="input-hint">
                {isCanada ? 'From selling investments (50% taxable)' : 'Long-term gains (held >1 year) taxed at 0/15/20%'}
              </span>
            </div>

            {!isCanada && (
              <div className="input-group">
                <label htmlFor="qualifiedDividends">
                  Qualified Dividends
                </label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="text"
                    id="qualifiedDividends"
                    value={qualifiedDividends}
                    onChange={(e) => setQualifiedDividends(e.target.value)}
                    onBlur={() => formatInputAsCurrency(qualifiedDividends, setQualifiedDividends)}
                    placeholder="0"
                  />
                </div>
                <span className="input-hint">Taxed at capital gains rates (0/15/20%)</span>
              </div>
            )}

            {isCanada && (
              <div className="input-group">
                <label htmlFor="eligibleDividends">
                  Eligible Dividends
                  <InfoTooltip termKey="eligible_dividends" />
                </label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="text"
                    id="eligibleDividends"
                    value={eligibleDividends}
                    onChange={(e) => setEligibleDividends(e.target.value)}
                    onBlur={() => formatInputAsCurrency(eligibleDividends, setEligibleDividends)}
                    placeholder="0"
                  />
                </div>
                <span className="input-hint">From Canadian public corporations</span>
              </div>
            )}

            <div className="input-group">
              <label htmlFor="year">Tax Year</label>
              <select
                id="year"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              >
                <option value="2024">2024</option>
                <option value="2025">2025</option>
              </select>
            </div>

            {!isCanada && (
              <div className="input-group">
                <label htmlFor="age">Age</label>
                <input
                  type="number"
                  id="age"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  min="18"
                  max="100"
                />
              </div>
            )}
          </div>
        </div>
      )}

      <button
        className="calculate-btn"
        onClick={() => {
          if (onBeforeCalculate) {
            onBeforeCalculate(handleCalculate);
          } else {
            handleCalculate();
          }
        }}
        disabled={loading}
      >
        {loading ? 'Calculating...' : 'Estimate Tax'}
      </button>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="tax-disclaimer">
        <strong>Disclaimer</strong>
        This calculator provides estimates for educational purposes only. Your actual tax may vary based on
        additional credits, deductions, and individual circumstances. Consult a qualified tax professional
        for personalized tax advice and filing.
      </div>
    </div>
  );

  const renderCanadaSummaryTab = (data: CanadaTaxCalculationResponse) => {
    const totalIncome = parseNumber(employmentIncome) + parseNumber(selfEmploymentIncome) +
                        parseNumber(otherIncome) + parseNumber(capitalGains) + parseNumber(eligibleDividends);

    return (
      <div className="summary-tab">
        <div className="big-numbers">
          <div className="big-number-card income">
            <span className="label">Gross Income</span>
            <span className="value">{formatCurrency(totalIncome)}</span>
          </div>
          <div className="big-number-card deductions">
            <span className="label">Total Deductions</span>
            <span className="value negative">{formatCurrency(data.summary.total_deductions)}</span>
          </div>
          <div className="big-number-card net">
            <span className="label">Take-Home Pay</span>
            <span className="value">{formatCurrency(data.summary.net_income)}</span>
          </div>
        </div>

        <div className="deductions-breakdown">
          <h3>Where Your Money Goes</h3>
          <div className="breakdown-bars">
            <div className="breakdown-item">
              <div className="bar-label">
                <span>Federal Tax</span>
                <span>{formatCurrency(data.federal_tax.net)}</span>
              </div>
              <div className="bar-container">
                <div className="bar federal" style={{ width: `${(data.federal_tax.net / totalIncome) * 100}%` }} />
              </div>
            </div>

            <div className="breakdown-item">
              <div className="bar-label">
                <span>Provincial Tax ({PROVINCE_CODE_TO_NAME[province]})</span>
                <span>{formatCurrency(data.provincial_tax.net)}</span>
              </div>
              <div className="bar-container">
                <div className="bar provincial" style={{ width: `${(data.provincial_tax.net / totalIncome) * 100}%` }} />
              </div>
            </div>

            <div className="breakdown-item">
              <div className="bar-label">
                <span>CPP Contributions</span>
                <span>{formatCurrency(data.cpp.total)}</span>
              </div>
              <div className="bar-container">
                <div className="bar cpp" style={{ width: `${(data.cpp.total / totalIncome) * 100}%` }} />
              </div>
            </div>

            <div className="breakdown-item">
              <div className="bar-label">
                <span>EI Premiums</span>
                <span>{formatCurrency(data.ei.premium)}</span>
              </div>
              <div className="bar-container">
                <div className="bar ei" style={{ width: `${(data.ei.premium / totalIncome) * 100}%` }} />
              </div>
            </div>

            <div className="breakdown-item take-home">
              <div className="bar-label">
                <span>Take-Home Pay</span>
                <span>{formatCurrency(data.summary.net_income)}</span>
              </div>
              <div className="bar-container">
                <div className="bar net" style={{ width: `${(data.summary.net_income / totalIncome) * 100}%` }} />
              </div>
            </div>
          </div>
        </div>

        {data.donation_credit && data.donation_credit.total_credit > 0 && (
          <div className="rates-card" style={{ marginBottom: 16 }}>
            <h3>Charitable Donation Tax Credit</h3>
            <div className="rates-grid">
              <div className="rate-item">
                <span className="rate-label">Donations</span>
                <span className="rate-value">{formatCurrency(data.donation_credit.eligible_amount)}</span>
              </div>
              <div className="rate-item">
                <span className="rate-label">Federal Credit</span>
                <span className="rate-value" style={{ color: '#059669' }}>{formatCurrency(data.donation_credit.federal_credit)}</span>
              </div>
              <div className="rate-item">
                <span className="rate-label">Provincial Credit</span>
                <span className="rate-value" style={{ color: '#059669' }}>{formatCurrency(data.donation_credit.provincial_credit)}</span>
              </div>
              <div className="rate-item">
                <span className="rate-label">Total Saved</span>
                <span className="rate-value" style={{ color: '#059669', fontWeight: 700 }}>{formatCurrency(data.donation_credit.total_credit)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="rates-card">
          <h3>Tax Rates</h3>
          <div className="rates-grid">
            <div className="rate-item">
              <span className="rate-label">Effective Tax Rate</span>
              <span className="rate-value">{data.summary.effective_tax_rate.toFixed(1)}%</span>
              <span className="rate-hint">Income tax / Gross income</span>
            </div>
            <div className="rate-item">
              <span className="rate-label">Total Deduction Rate</span>
              <span className="rate-value">{data.summary.total_effective_rate.toFixed(1)}%</span>
              <span className="rate-hint">All deductions / Employment income</span>
            </div>
            <div className="rate-item">
              <span className="rate-label">Marginal Rate</span>
              <span className="rate-value">{data.marginal_rates.combined.toFixed(1)}%</span>
              <span className="rate-hint">Tax on next dollar earned</span>
            </div>
          </div>
        </div>

        <div className="monthly-card">
          <h3>Monthly Take-Home</h3>
          <div className="monthly-value">{formatCurrency(data.summary.net_income / 12)}</div>
          <div className="monthly-details">
            <span>Bi-weekly: {formatCurrency(data.summary.net_income / 26)}</span>
            <span>Weekly: {formatCurrency(data.summary.net_income / 52)}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderUSASummaryTab = (data: USATaxCalculationResponse) => {
    const totalIncome = parseNumber(employmentIncome) + parseNumber(selfEmploymentIncome) +
                        parseNumber(otherIncome) + parseNumber(capitalGains) + parseNumber(qualifiedDividends);

    return (
      <div className="summary-tab">
        <div className="big-numbers">
          <div className="big-number-card income">
            <span className="label">Gross Income</span>
            <span className="value">{formatCurrency(totalIncome)}</span>
          </div>
          <div className="big-number-card deductions">
            <span className="label">Total Deductions</span>
            <span className="value negative">{formatCurrency(data.summary.total_deductions)}</span>
          </div>
          <div className="big-number-card net">
            <span className="label">Take-Home Pay</span>
            <span className="value">{formatCurrency(data.summary.net_income)}</span>
          </div>
        </div>

        <div className="deductions-breakdown">
          <h3>Where Your Money Goes</h3>
          <div className="breakdown-bars">
            <div className="breakdown-item">
              <div className="bar-label">
                <span>Federal Tax (Ordinary Income)</span>
                <span>{formatCurrency(data.federal_tax.ordinary_tax)}</span>
              </div>
              <div className="bar-container">
                <div className="bar federal" style={{ width: `${(data.federal_tax.ordinary_tax / totalIncome) * 100}%` }} />
              </div>
            </div>

            {data.federal_tax.capital_gains_tax > 0 && (
              <div className="breakdown-item">
                <div className="bar-label">
                  <span>Federal Tax (Cap Gains @ {(data.federal_tax.capital_gains_rate * 100).toFixed(0)}%)</span>
                  <span>{formatCurrency(data.federal_tax.capital_gains_tax)}</span>
                </div>
                <div className="bar-container">
                  <div className="bar federal" style={{ width: `${(data.federal_tax.capital_gains_tax / totalIncome) * 100}%`, opacity: 0.7 }} />
                </div>
              </div>
            )}

            {data.federal_tax.niit > 0 && (
              <div className="breakdown-item">
                <div className="bar-label">
                  <span>NIIT (3.8% Investment Tax)</span>
                  <span>{formatCurrency(data.federal_tax.niit)}</span>
                </div>
                <div className="bar-container">
                  <div className="bar federal" style={{ width: `${(data.federal_tax.niit / totalIncome) * 100}%`, opacity: 0.5 }} />
                </div>
              </div>
            )}

            <div className="breakdown-item">
              <div className="bar-label">
                <span>State Tax ({data.state_tax.state})</span>
                <span>{formatCurrency(data.state_tax.net)}</span>
              </div>
              <div className="bar-container">
                <div className="bar provincial" style={{ width: `${(data.state_tax.net / totalIncome) * 100}%` }} />
              </div>
            </div>

            <div className="breakdown-item">
              <div className="bar-label">
                <span>Social Security</span>
                <span>{formatCurrency(data.fica.social_security)}</span>
              </div>
              <div className="bar-container">
                <div className="bar cpp" style={{ width: `${(data.fica.social_security / totalIncome) * 100}%` }} />
              </div>
            </div>

            <div className="breakdown-item">
              <div className="bar-label">
                <span>Medicare</span>
                <span>{formatCurrency(data.fica.medicare)}</span>
              </div>
              <div className="bar-container">
                <div className="bar ei" style={{ width: `${(data.fica.medicare / totalIncome) * 100}%` }} />
              </div>
            </div>

            <div className="breakdown-item take-home">
              <div className="bar-label">
                <span>Take-Home Pay</span>
                <span>{formatCurrency(data.summary.net_income)}</span>
              </div>
              <div className="bar-container">
                <div className="bar net" style={{ width: `${(data.summary.net_income / totalIncome) * 100}%` }} />
              </div>
            </div>
          </div>
        </div>

        <div className="rates-card">
          <h3>Tax Rates</h3>
          <div className="rates-grid">
            <div className="rate-item">
              <span className="rate-label">Effective Tax Rate</span>
              <span className="rate-value">{data.summary.effective_tax_rate.toFixed(1)}%</span>
              <span className="rate-hint">Income tax / Gross income</span>
            </div>
            <div className="rate-item">
              <span className="rate-label">Total Deduction Rate</span>
              <span className="rate-value">{data.summary.total_effective_rate.toFixed(1)}%</span>
              <span className="rate-hint">All deductions / Gross income</span>
            </div>
            <div className="rate-item">
              <span className="rate-label">Marginal Rate</span>
              <span className="rate-value">{data.marginal_rates.combined.toFixed(1)}%</span>
              <span className="rate-hint">Tax on next dollar earned</span>
            </div>
            {(parseNumber(capitalGains) > 0 || parseNumber(qualifiedDividends) > 0) && (
              <div className="rate-item">
                <span className="rate-label">Cap Gains Rate</span>
                <span className="rate-value">{data.marginal_rates.federal_cap_gains.toFixed(0)}%</span>
                <span className="rate-hint">Preferential rate on LTCG/dividends</span>
              </div>
            )}
          </div>
        </div>

        <div className="monthly-card">
          <h3>Monthly Take-Home</h3>
          <div className="monthly-value">{formatCurrency(data.summary.net_income / 12)}</div>
          <div className="monthly-details">
            <span>Bi-weekly: {formatCurrency(data.summary.net_income / 26)}</span>
            <span>Weekly: {formatCurrency(data.summary.net_income / 52)}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderSummaryTab = () => {
    if (!results) return null;

    if (isCanadaResponse(results)) {
      return renderCanadaSummaryTab(results);
    } else if (isUSAResponse(results)) {
      return renderUSASummaryTab(results);
    }
    return null;
  };

  const renderCanadaBreakdownTab = (data: CanadaTaxCalculationResponse) => (
    <div className="breakdown-tab">
      <table className="breakdown-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr className="section-header">
            <td colSpan={2}>Income</td>
          </tr>
          <tr>
            <td>Employment Income</td>
            <td className="positive">{formatCurrency(parseNumber(employmentIncome))}</td>
          </tr>
          {parseNumber(selfEmploymentIncome) > 0 && (
            <tr>
              <td>Self-Employment Income</td>
              <td className="positive">{formatCurrency(parseNumber(selfEmploymentIncome))}</td>
            </tr>
          )}
          {parseNumber(otherIncome) > 0 && (
            <tr>
              <td>Other Income</td>
              <td className="positive">{formatCurrency(parseNumber(otherIncome))}</td>
            </tr>
          )}
          {parseNumber(capitalGains) > 0 && (
            <tr>
              <td>Taxable Capital Gains (50%)</td>
              <td className="positive">{formatCurrency(parseNumber(capitalGains) * 0.5)}</td>
            </tr>
          )}
          {parseNumber(eligibleDividends) > 0 && (
            <tr>
              <td>Grossed-Up Dividends (138%)</td>
              <td className="positive">{formatCurrency(parseNumber(eligibleDividends) * 1.38)}</td>
            </tr>
          )}
          <tr className="subtotal">
            <td>Gross Income</td>
            <td>{formatCurrency(data.summary.gross_income)}</td>
          </tr>

          <tr className="section-header">
            <td colSpan={2}>Deductions</td>
          </tr>
          {parseNumber(rrspContribution) > 0 && (
            <tr>
              <td>RRSP Contribution</td>
              <td className="negative">-{formatCurrency(parseNumber(rrspContribution))}</td>
            </tr>
          )}
          <tr className="subtotal">
            <td>Taxable Income</td>
            <td>{formatCurrency(data.summary.taxable_income)}</td>
          </tr>

          <tr className="section-header">
            <td colSpan={2}>Federal Tax</td>
          </tr>
          <tr>
            <td>Gross Federal Tax</td>
            <td className="negative">-{formatCurrency(data.federal_tax.gross)}</td>
          </tr>
          <tr>
            <td>Basic Personal Amount Credit</td>
            <td className="positive">+{formatCurrency(data.federal_tax.credits)}</td>
          </tr>
          {data.federal_tax.dividend_credit > 0 && (
            <tr>
              <td>Dividend Tax Credit</td>
              <td className="positive">+{formatCurrency(data.federal_tax.dividend_credit)}</td>
            </tr>
          )}
          {data.donation_credit && data.donation_credit.federal_credit > 0 && (
            <tr>
              <td>Charitable Donation Credit</td>
              <td className="positive">+{formatCurrency(data.donation_credit.federal_credit)}</td>
            </tr>
          )}
          <tr className="subtotal">
            <td>Net Federal Tax</td>
            <td className="negative">-{formatCurrency(data.federal_tax.net)}</td>
          </tr>

          <tr className="section-header">
            <td colSpan={2}>Provincial Tax ({data.provincial_tax.province})</td>
          </tr>
          <tr>
            <td>Gross Provincial Tax</td>
            <td className="negative">-{formatCurrency(data.provincial_tax.gross)}</td>
          </tr>
          <tr>
            <td>Basic Personal Amount Credit</td>
            <td className="positive">+{formatCurrency(data.provincial_tax.credits)}</td>
          </tr>
          {data.provincial_tax.dividend_credit > 0 && (
            <tr>
              <td>Dividend Tax Credit</td>
              <td className="positive">+{formatCurrency(data.provincial_tax.dividend_credit)}</td>
            </tr>
          )}
          {data.donation_credit && data.donation_credit.provincial_credit > 0 && (
            <tr>
              <td>Charitable Donation Credit</td>
              <td className="positive">+{formatCurrency(data.donation_credit.provincial_credit)}</td>
            </tr>
          )}
          <tr className="subtotal">
            <td>Net Provincial Tax</td>
            <td className="negative">-{formatCurrency(data.provincial_tax.net)}</td>
          </tr>

          <tr className="section-header">
            <td colSpan={2}>CPP Contributions</td>
          </tr>
          {data.cpp.employment.total > 0 && (
            <tr>
              <td>CPP (Employee Portion)</td>
              <td className="negative">-{formatCurrency(data.cpp.employment.total)}</td>
            </tr>
          )}
          {data.cpp.self_employment.total > 0 && (
            <tr>
              <td>CPP (Self-Employed - Both Portions)</td>
              <td className="negative">-{formatCurrency(data.cpp.self_employment.total)}</td>
            </tr>
          )}

          <tr className="section-header">
            <td colSpan={2}>EI Premiums</td>
          </tr>
          <tr>
            <td>Employment Insurance</td>
            <td className="negative">-{formatCurrency(data.ei.premium)}</td>
          </tr>

          <tr className="total">
            <td>Total Deductions</td>
            <td className="negative">-{formatCurrency(data.summary.total_deductions)}</td>
          </tr>
          <tr className="total net-income">
            <td>Net Income (Take-Home)</td>
            <td className="positive">{formatCurrency(data.summary.net_income)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const renderUSABreakdownTab = (data: USATaxCalculationResponse) => (
    <div className="breakdown-tab">
      <table className="breakdown-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr className="section-header">
            <td colSpan={2}>Income</td>
          </tr>
          <tr>
            <td>Wages (W-2)</td>
            <td className="positive">{formatCurrency(parseNumber(employmentIncome))}</td>
          </tr>
          {parseNumber(selfEmploymentIncome) > 0 && (
            <tr>
              <td>Self-Employment Income</td>
              <td className="positive">{formatCurrency(parseNumber(selfEmploymentIncome))}</td>
            </tr>
          )}
          {parseNumber(otherIncome) > 0 && (
            <tr>
              <td>Other Income</td>
              <td className="positive">{formatCurrency(parseNumber(otherIncome))}</td>
            </tr>
          )}
          {parseNumber(capitalGains) > 0 && (
            <tr>
              <td>Long-Term Capital Gains</td>
              <td className="positive">{formatCurrency(parseNumber(capitalGains))}</td>
            </tr>
          )}
          {parseNumber(qualifiedDividends) > 0 && (
            <tr>
              <td>Qualified Dividends</td>
              <td className="positive">{formatCurrency(parseNumber(qualifiedDividends))}</td>
            </tr>
          )}
          <tr className="subtotal">
            <td>Gross Income</td>
            <td>{formatCurrency(data.summary.gross_income)}</td>
          </tr>
          {(data.summary.ordinary_income > 0 || data.summary.preferential_income > 0) && (
            <>
              <tr>
                <td style={{ paddingLeft: '20px', fontStyle: 'italic' }}>Ordinary Income</td>
                <td style={{ fontStyle: 'italic' }}>{formatCurrency(data.summary.ordinary_income)}</td>
              </tr>
              <tr>
                <td style={{ paddingLeft: '20px', fontStyle: 'italic' }}>Preferential Income (LTCG + Qual Div)</td>
                <td style={{ fontStyle: 'italic' }}>{formatCurrency(data.summary.preferential_income)}</td>
              </tr>
            </>
          )}

          <tr className="section-header">
            <td colSpan={2}>Deductions</td>
          </tr>
          <tr>
            <td>Standard Deduction ({data.filing_status === 'married' ? 'Married' : 'Single'})</td>
            <td className="negative">-{formatCurrency(data.deductions_used.standard_deduction)}</td>
          </tr>
          {data.deductions_used.retirement_contribution > 0 && (
            <tr>
              <td>401(k)/IRA Contribution</td>
              <td className="negative">-{formatCurrency(data.deductions_used.retirement_contribution)}</td>
            </tr>
          )}
          {data.deductions_used.self_employment_deduction > 0 && (
            <tr>
              <td>Self-Employment Tax Deduction</td>
              <td className="negative">-{formatCurrency(data.deductions_used.self_employment_deduction)}</td>
            </tr>
          )}
          <tr className="subtotal">
            <td>Taxable Income</td>
            <td>{formatCurrency(data.summary.taxable_income)}</td>
          </tr>

          <tr className="section-header">
            <td colSpan={2}>Federal Tax</td>
          </tr>
          <tr>
            <td>Tax on Ordinary Income</td>
            <td className="negative">-{formatCurrency(data.federal_tax.ordinary_tax)}</td>
          </tr>
          {data.federal_tax.capital_gains_tax > 0 && (
            <tr>
              <td>Tax on LTCG/Qual Dividends ({(data.federal_tax.capital_gains_rate * 100).toFixed(0)}%)</td>
              <td className="negative">-{formatCurrency(data.federal_tax.capital_gains_tax)}</td>
            </tr>
          )}
          {data.federal_tax.niit > 0 && (
            <tr>
              <td>Net Investment Income Tax (3.8%)</td>
              <td className="negative">-{formatCurrency(data.federal_tax.niit)}</td>
            </tr>
          )}
          <tr className="subtotal">
            <td>Total Federal Tax</td>
            <td className="negative">-{formatCurrency(data.federal_tax.net)}</td>
          </tr>

          <tr className="section-header">
            <td colSpan={2}>State Tax ({data.state_tax.state})</td>
          </tr>
          <tr>
            <td>State Income Tax {data.state_tax.note && `(${data.state_tax.note})`}</td>
            <td className="negative">-{formatCurrency(data.state_tax.net)}</td>
          </tr>

          <tr className="section-header">
            <td colSpan={2}>FICA (Social Security & Medicare)</td>
          </tr>
          {data.fica.employment.total > 0 && (
            <>
              <tr>
                <td>Social Security (6.2%)</td>
                <td className="negative">-{formatCurrency(data.fica.employment.social_security)}</td>
              </tr>
              <tr>
                <td>Medicare (1.45%)</td>
                <td className="negative">-{formatCurrency(data.fica.employment.medicare)}</td>
              </tr>
              {data.fica.employment.additional_medicare > 0 && (
                <tr>
                  <td>Additional Medicare (0.9%)</td>
                  <td className="negative">-{formatCurrency(data.fica.employment.additional_medicare)}</td>
                </tr>
              )}
            </>
          )}
          {data.fica.self_employment.total > 0 && (
            <>
              <tr>
                <td>Self-Employment Social Security (12.4%)</td>
                <td className="negative">-{formatCurrency(data.fica.self_employment.social_security)}</td>
              </tr>
              <tr>
                <td>Self-Employment Medicare (2.9%)</td>
                <td className="negative">-{formatCurrency(data.fica.self_employment.medicare)}</td>
              </tr>
              {data.fica.self_employment.additional_medicare > 0 && (
                <tr>
                  <td>Additional Medicare (0.9%)</td>
                  <td className="negative">-{formatCurrency(data.fica.self_employment.additional_medicare)}</td>
                </tr>
              )}
            </>
          )}

          <tr className="total">
            <td>Total Deductions</td>
            <td className="negative">-{formatCurrency(data.summary.total_deductions)}</td>
          </tr>
          <tr className="total net-income">
            <td>Net Income (Take-Home)</td>
            <td className="positive">{formatCurrency(data.summary.net_income)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const renderBreakdownTab = () => {
    if (!results) return null;

    if (isCanadaResponse(results)) {
      return renderCanadaBreakdownTab(results);
    } else if (isUSAResponse(results)) {
      return renderUSABreakdownTab(results);
    }
    return null;
  };

  const renderCanadaBracketsTab = (data: CanadaTaxCalculationResponse) => (
    <div className="brackets-tab">
      <div className="brackets-section">
        <h3>Federal Tax Brackets</h3>
        <table className="brackets-table">
          <thead>
            <tr>
              <th>Income Range</th>
              <th>Rate</th>
              <th>Taxable in Bracket</th>
              <th>Tax</th>
            </tr>
          </thead>
          <tbody>
            {data.federal_tax.brackets.map((bracket, idx) => (
              <tr key={idx} className={bracket.taxable > 0 ? 'active' : ''}>
                <td>
                  {formatCurrency(bracket.min)} - {bracket.max ? formatCurrency(bracket.max) : 'No limit'}
                </td>
                <td>{(bracket.rate * 100).toFixed(1)}%</td>
                <td>{formatCurrency(bracket.taxable)}</td>
                <td>{formatCurrency(bracket.tax)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="brackets-section">
        <h3>{data.provincial_tax.province} Tax Brackets</h3>
        <table className="brackets-table">
          <thead>
            <tr>
              <th>Income Range</th>
              <th>Rate</th>
              <th>Taxable in Bracket</th>
              <th>Tax</th>
            </tr>
          </thead>
          <tbody>
            {data.provincial_tax.brackets.map((bracket, idx) => (
              <tr key={idx} className={bracket.taxable > 0 ? 'active' : ''}>
                <td>
                  {formatCurrency(bracket.min)} - {bracket.max ? formatCurrency(bracket.max) : 'No limit'}
                </td>
                <td>{(bracket.rate * 100).toFixed(2)}%</td>
                <td>{formatCurrency(bracket.taxable)}</td>
                <td>{formatCurrency(bracket.tax)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="marginal-rate-card">
        <h3>Your Marginal Tax Rate</h3>
        <div className="marginal-breakdown">
          <div className="marginal-item">
            <span>Federal</span>
            <span>{data.marginal_rates.federal}%</span>
          </div>
          <div className="marginal-item">
            <span>Provincial</span>
            <span>{data.marginal_rates.provincial}%</span>
          </div>
          <div className="marginal-item total">
            <span>Combined</span>
            <span>{data.marginal_rates.combined}%</span>
          </div>
        </div>
        <p className="marginal-hint">
          This is the tax rate on your next dollar of income.
          An RRSP contribution saves you {data.marginal_rates.combined}% in taxes.
        </p>
      </div>
    </div>
  );

  const renderUSABracketsTab = (data: USATaxCalculationResponse) => (
    <div className="brackets-tab">
      <div className="brackets-section">
        <h3>Federal Tax Brackets ({data.filing_status === 'married' ? 'Married Filing Jointly' : 'Single'})</h3>
        <table className="brackets-table">
          <thead>
            <tr>
              <th>Income Range</th>
              <th>Rate</th>
              <th>Taxable in Bracket</th>
              <th>Tax</th>
            </tr>
          </thead>
          <tbody>
            {data.federal_tax.brackets.map((bracket, idx) => (
              <tr key={idx} className={bracket.taxable > 0 ? 'active' : ''}>
                <td>
                  {formatCurrency(bracket.min)} - {bracket.max ? formatCurrency(bracket.max) : 'No limit'}
                </td>
                <td>{(bracket.rate * 100).toFixed(0)}%</td>
                <td>{formatCurrency(bracket.taxable)}</td>
                <td>{formatCurrency(bracket.tax)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(parseNumber(capitalGains) > 0 || parseNumber(qualifiedDividends) > 0) && (
        <div className="brackets-section">
          <h3>Long-Term Capital Gains & Qualified Dividends Rates</h3>
          <table className="brackets-table">
            <thead>
              <tr>
                <th>Total Taxable Income</th>
                <th>Rate</th>
              </tr>
            </thead>
            <tbody>
              <tr className={data.federal_tax.capital_gains_rate === 0 ? 'active' : ''}>
                <td>{data.filing_status === 'married' ? '$0 - $94,050' : '$0 - $47,025'}</td>
                <td>0%</td>
              </tr>
              <tr className={data.federal_tax.capital_gains_rate === 0.15 ? 'active' : ''}>
                <td>{data.filing_status === 'married' ? '$94,050 - $583,750' : '$47,025 - $518,900'}</td>
                <td>15%</td>
              </tr>
              <tr className={data.federal_tax.capital_gains_rate === 0.20 ? 'active' : ''}>
                <td>{data.filing_status === 'married' ? 'Over $583,750' : 'Over $518,900'}</td>
                <td>20%</td>
              </tr>
            </tbody>
          </table>
          <p className="brackets-note">
            Your rate: <strong>{(data.federal_tax.capital_gains_rate * 100).toFixed(0)}%</strong> on {formatCurrency(parseNumber(capitalGains) + parseNumber(qualifiedDividends))} preferential income
          </p>
        </div>
      )}

      {data.federal_tax.niit_applies && (
        <div className="brackets-section niit-info">
          <h3>Net Investment Income Tax (NIIT)</h3>
          <p>
            NIIT is a 3.8% tax on investment income (capital gains, dividends, interest, rental income)
            for taxpayers with modified AGI above {data.filing_status === 'married' ? '$250,000' : '$200,000'}.
          </p>
          {data.federal_tax.niit > 0 ? (
            <p className="niit-amount">Your NIIT: <strong>{formatCurrency(data.federal_tax.niit)}</strong></p>
          ) : (
            <p className="niit-amount">You are subject to NIIT but the amount is $0 based on your income mix.</p>
          )}
        </div>
      )}

      {data.state_tax.brackets.length > 0 && (
        <div className="brackets-section">
          <h3>{data.state_tax.state} Tax Brackets</h3>
          <table className="brackets-table">
            <thead>
              <tr>
                <th>Income Range</th>
                <th>Rate</th>
                <th>Taxable in Bracket</th>
                <th>Tax</th>
              </tr>
            </thead>
            <tbody>
              {data.state_tax.brackets.map((bracket, idx) => (
                <tr key={idx} className={bracket.taxable > 0 ? 'active' : ''}>
                  <td>
                    {formatCurrency(bracket.min)} - {bracket.max ? formatCurrency(bracket.max) : 'No limit'}
                  </td>
                  <td>{(bracket.rate * 100).toFixed(2)}%</td>
                  <td>{formatCurrency(bracket.taxable)}</td>
                  <td>{formatCurrency(bracket.tax)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.state_tax.note && data.state_tax.brackets.length === 0 && (
        <div className="brackets-section">
          <h3>{data.state_tax.state} State Tax</h3>
          <p className="state-note">{data.state_tax.note}</p>
        </div>
      )}

      <div className="marginal-rate-card">
        <h3>Your Marginal Tax Rate</h3>
        <div className="marginal-breakdown">
          <div className="marginal-item">
            <span>Federal (Ordinary)</span>
            <span>{data.marginal_rates.federal}%</span>
          </div>
          {(parseNumber(capitalGains) > 0 || parseNumber(qualifiedDividends) > 0) && (
            <div className="marginal-item">
              <span>Federal (Cap Gains)</span>
              <span>{data.marginal_rates.federal_cap_gains}%</span>
            </div>
          )}
          <div className="marginal-item">
            <span>State</span>
            <span>{data.marginal_rates.state}%</span>
          </div>
          <div className="marginal-item total">
            <span>Combined (Ordinary)</span>
            <span>{data.marginal_rates.combined}%</span>
          </div>
        </div>
        <p className="marginal-hint">
          This is the tax rate on your next dollar of income.
          A 401(k) contribution saves you {data.marginal_rates.combined}% in taxes.
        </p>
      </div>
    </div>
  );

  const renderBracketsTab = () => {
    if (!results) return null;

    if (isCanadaResponse(results)) {
      return renderCanadaBracketsTab(results);
    } else if (isUSAResponse(results)) {
      return renderUSABracketsTab(results);
    }
    return null;
  };

  const renderRRSPImpactTab = () => {
    if (rrspImpactLoading) {
      return <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading RRSP impact analysis...</div>;
    }
    if (!rrspImpactData || rrspImpactData.length === 0) {
      return <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No data available</div>;
    }

    const currentRrsp = parseNumber(rrspContribution);

    return (
      <div>
        <h3 style={{ color: '#1e3a5f', marginBottom: 8 }}>RRSP Contribution Impact</h3>
        <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 16 }}>
          See how different RRSP contribution amounts affect your tax bill and take-home pay.
        </p>

        {results && isCanadaResponse(results) && results.marginal_rates && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 14, marginBottom: 16, fontSize: '0.9rem' }}>
            At your <strong>{results.marginal_rates.combined.toFixed(1)}%</strong> combined marginal rate,
            every <strong>$1,000</strong> in RRSP contributions saves approximately <strong>{formatCurrency(results.marginal_rates.combined * 10)}</strong> in tax.
          </div>
        )}

        <table className="paycheck-stub" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>RRSP Contribution</th>
              <th style={{ textAlign: 'right' }}>Federal Tax</th>
              <th style={{ textAlign: 'right' }}>Provincial Tax</th>
              <th style={{ textAlign: 'right' }}>Net Income</th>
              <th style={{ textAlign: 'right' }}>Tax Savings</th>
            </tr>
          </thead>
          <tbody>
            {rrspImpactData.map((scenario) => {
              const isCurrentRow = scenario.rrsp_amount === currentRrsp;
              return (
                <tr key={scenario.rrsp_amount} style={isCurrentRow ? { background: '#fef9c3', fontWeight: 600 } : {}}>
                  <td>{formatCurrency(scenario.rrsp_amount)}{isCurrentRow ? ' *' : ''}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(scenario.federal_tax)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(scenario.provincial_tax)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(scenario.net_income)}</td>
                  <td style={{ textAlign: 'right', color: scenario.tax_savings > 0 ? '#059669' : undefined }}>
                    {scenario.tax_savings > 0 ? '+' : ''}{formatCurrency(scenario.tax_savings)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: 8 }}>
          * Your current RRSP contribution amount
        </p>
      </div>
    );
  };

  return (
    <div className={embedded ? '' : 'income-tax-calculator'}>
      {!embedded && (
        <>
          <StructuredData
            type="calculator"
            data={{
              name: isCanada ? "Canadian Income Tax Estimator" : "US Income Tax Estimator",
              description: isCanada
                ? "Estimate your Canadian income tax, CPP contributions, and EI premiums"
                : "Estimate your US income tax, Social Security, and Medicare"
            }}
          />

          <div className="calculator-header">
            <h1>Income Tax Estimator</h1>
            <p>
              {isCanada
                ? 'Estimate your Canadian income tax, CPP, and EI contributions'
                : 'Estimate your US income tax, Social Security, and Medicare'}
            </p>
          </div>
        </>
      )}

      <div className="calculator-content">
        <div className="input-panel">
          {renderInputSection()}
        </div>

        {results && (
          <div className="results-panel">
            <div className="results-tabs">
              <button
                className={`tab ${resultsTab === 'summary' ? 'active' : ''}`}
                onClick={() => setResultsTab('summary')}
              >
                Summary
              </button>
              <button
                className={`tab ${resultsTab === 'breakdown' ? 'active' : ''}`}
                onClick={() => setResultsTab('breakdown')}
              >
                Detailed Breakdown
              </button>
              <button
                className={`tab ${resultsTab === 'brackets' ? 'active' : ''}`}
                onClick={() => setResultsTab('brackets')}
              >
                Tax Brackets
              </button>
              {isCanada && (
                <button
                  className={`tab ${resultsTab === 'rrsp-impact' ? 'active' : ''}`}
                  onClick={async () => {
                    setResultsTab('rrsp-impact');
                    if (!rrspImpactData) {
                      setRrspImpactLoading(true);
                      try {
                        const resp = await incomeTaxAPI.getRRSPImpact({
                          employment_income: parseNumber(employmentIncome),
                          self_employment_income: parseNumber(selfEmploymentIncome),
                          other_income: parseNumber(otherIncome),
                          capital_gains: parseNumber(capitalGains),
                          eligible_dividends: parseNumber(eligibleDividends),
                          rrsp_contribution: parseNumber(rrspContribution),
                          charitable_donations: parseNumber(charitableDonations),
                          province: PROVINCE_CODE_TO_NAME[province] || 'Ontario',
                          age: parseInt(age) || 35,
                          year: parseInt(year) || 2024,
                        });
                        setRrspImpactData(resp.scenarios);
                      } catch {
                        setRrspImpactData([]);
                      } finally {
                        setRrspImpactLoading(false);
                      }
                    }
                  }}
                  disabled={rrspImpactLoading}
                >
                  {rrspImpactLoading ? 'Loading...' : 'RRSP Impact'}
                </button>
              )}
            </div>

            <div className="results-content">
              {resultsTab === 'summary' && renderSummaryTab()}
              {resultsTab === 'breakdown' && renderBreakdownTab()}
              {resultsTab === 'brackets' && renderBracketsTab()}
              {resultsTab === 'rrsp-impact' && renderRRSPImpactTab()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncomeTaxCalculator;
